import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useOrganization } from '@/contexts/OrganizationContext';

const DEFAULT_CURRENCY = 'USD';
const DEFAULT_LOCALE = 'en-US';

const normalizeCurrency = (value: string): string => {
  const trimmed = value.trim().toUpperCase();
  return trimmed.length === 3 ? trimmed : DEFAULT_CURRENCY;
};

export const useCurrencyFormatter = (options?: { locale?: string }) => {
  const { organization } = useOrganization();
  const locale = options?.locale ?? DEFAULT_LOCALE;

  const currency = useMemo(
    () => normalizeCurrency(organization?.default_currency ?? DEFAULT_CURRENCY),
    [organization?.default_currency]
  );

  const cacheRef = useRef<Map<string, Intl.NumberFormat>>(new Map());

  // Locale changes require a fresh set of formatters.
  useEffect(() => {
    cacheRef.current = new Map();
  }, [locale]);

  const getFormatter = useCallback(
    (currencyCode: string) => {
      const normalized = normalizeCurrency(currencyCode);
      const cached = cacheRef.current.get(normalized);
      if (cached) return cached;
      // currencyDisplay: 'narrowSymbol' renders just the currency symbol (e.g.
      // "$" for AUD instead of "A$") in en-* locales. Multi-currency contexts
      // (PoActionBar, OrderCreate) label the currency code explicitly elsewhere
      // in the UI, so dropping the disambiguating prefix here matches the rest
      // of the codebase (`AdditionalCostsSection.tsx` already uses this option).
      const formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: normalized,
        currencyDisplay: 'narrowSymbol',
      });
      cacheRef.current.set(normalized, formatter);
      return formatter;
    },
    [locale]
  );

  const formatter = useMemo(() => getFormatter(currency), [currency, getFormatter]);

  const format = useCallback(
    (value: number, currencyOverride?: string | null) => {
      const normalized = normalizeCurrency(currencyOverride ?? currency);
      return getFormatter(normalized).format(value);
    },
    [currency, getFormatter]
  );

  /**
   * "22.50 USD": an amount with its own code; a null code is the org
   * currency. A string amount (a numeric read back from JSON) is accepted.
   */
  const formatWithCode = useCallback(
    (value: number | string, code?: string | null) => `${Number(value).toFixed(2)} ${code ?? currency}`,
    [currency]
  );

  /**
   * A Default cost as shown: format(amount) when it is in the org currency
   * (null, undefined from an older backend, or the org code itself); else
   * "22.50 USD ≈ $32.23", the "≈" part only when the org amount is known.
   */
  const formatConverted = useCallback(
    (amount: number, amountCurrency?: string | null, orgAmount?: number | null) => {
      if (amountCurrency == null || amountCurrency === currency) return format(amount);
      const own = formatWithCode(amount, amountCurrency);
      return orgAmount == null ? own : `${own} ≈ ${format(orgAmount)}`;
    },
    [currency, format, formatWithCode]
  );

  return { currency, formatter, format, formatWithCode, formatConverted };
};
